/**
 * Hook d'orchestration de la liste des profils.
 * Responsabilité: gérer la recherche, la pagination et l'appel API, sans logique de rendu.
 */
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetUtilisateursByClientQuery } from 'lauria/api/utilisateurs'
import { utilisateurListItemToProfileListRow } from 'lauria/pages/profiles/mappers/utilisateurListItemToProfileListRow'
import type { ProfileListRow } from 'lauria/pages/profiles/profilesListRow.types'
import { useEffect, useMemo, useState } from 'react'

const DEFAULT_PAGE_SIZE = 20

export interface UseProfilesListArgs {
  idClient?: number
  pageSize?: number
}

export interface UseProfilesListResult {
  rows: ProfileListRow[]
  totalItems: number
  page: number
  pageSize: number
  pageCount: number
  searchInput: string
  setSearchInput: (value: string) => void
  applySearch: () => void
  setPage: (page: number) => void
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  refetch: () => void
}

function clampPage(page: number, pageCount: number): number {
  if (pageCount <= 0) return 1
  return Math.max(1, Math.min(pageCount, page))
}

export function useProfilesList({
  idClient,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseProfilesListArgs): UseProfilesListResult {
  const [searchInput, setSearchInput] = useState<string>('')
  const [appliedSearch, setAppliedSearch] = useState<string>('')
  const [page, setPageState] = useState<number>(1)

  const queryArgs = idClient
    ? {
        idClient,
        params: {
          page,
          itemsPerPage: pageSize,
          search: appliedSearch,
        },
      }
    : skipToken

  const { data, isLoading, isFetching, isError, refetch } =
    useGetUtilisateursByClientQuery(queryArgs)

  const totalItems = data?.totalItems ?? 0
  const pageCount = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 0

  const rows = useMemo<ProfileListRow[]>(() => {
    return (data?.member ?? []).map(utilisateurListItemToProfileListRow)
  }, [data?.member])

  useEffect(() => {
    // Si le backend renvoie moins de pages (ex: nouvelle recherche), on garde `page` dans un intervalle valide.
    setPageState((current) => clampPage(current, pageCount))
  }, [pageCount])

  const setPage = (nextPage: number) => {
    setPageState((prev) => {
      const desired = Number.isFinite(nextPage) ? nextPage : prev
      return clampPage(desired, pageCount)
    })
  }

  const applySearch = () => {
    // Invariant UI: une nouvelle recherche repart en page 1.
    setPageState(1)
    setAppliedSearch(searchInput.trim())
  }

  return {
    rows,
    totalItems,
    page,
    pageSize,
    pageCount,
    searchInput,
    setSearchInput,
    applySearch,
    setPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  }
}
